import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertCircle, AlertTriangle, CheckCircle2, Clock, Receipt, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api, type ExactStatus } from '@/lib/api';
import { EXACT_STATUS_LABELS } from '@/lib/labels';
import { formatAmount, formatDateISO } from '@/lib/utils';

interface Search {
	entity_code?: string;
	exact_status?: ExactStatus;
	// `all=1` betekent expliciet "Alle"-tab (geen status-filter). Zonder
	// exact_status én zonder all=1 is de default Werklijst (pending_review).
	all?: boolean;
}

const TAB_STATUSES: Array<{ key: ExactStatus | 'all'; label: string; icon: typeof Clock }> = [
	{ key: 'pending_review', label: 'Werklijst', icon: Clock },
	{ key: 'approved', label: 'Goedgekeurd', icon: CheckCircle2 },
	{ key: 'sent', label: 'Geboekt', icon: Receipt },
	{ key: 'failed', label: 'Fout', icon: AlertCircle },
	{ key: 'all', label: 'Alle', icon: Receipt },
];

export const Route = createFileRoute('/inkoopfacturen/')({
	component: InkoopfacturenList,
	validateSearch: (s: Record<string, unknown>): Search => ({
		entity_code: typeof s.entity_code === 'string' ? s.entity_code : undefined,
		exact_status: isExactStatus(s.exact_status) ? s.exact_status : undefined,
		all: s.all === '1' || s.all === true ? true : undefined,
	}),
});

function isExactStatus(v: unknown): v is ExactStatus {
	return (
		typeof v === 'string' &&
		['pending_review', 'approved', 'sent', 'failed', 'not_applicable', 'already_present'].includes(v)
	);
}

function InkoopfacturenList() {
	const search = Route.useSearch();
	const navigate = useNavigate({ from: Route.fullPath });
	const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me() });
	const entitiesQuery = useQuery({ queryKey: ['entities'], queryFn: () => api.entities() });

	// Default-tab = Werklijst. "Alle" is alleen actief bij expliciet ?all=1.
	const isAll = search.all === true;
	const activeStatus: ExactStatus | undefined = isAll ? undefined : search.exact_status ?? 'pending_review';

	const docsQuery = useQuery({
		queryKey: ['inkoopfacturen', { ...search, _resolved: activeStatus }],
		queryFn: () =>
			api.inkoopfacturen({
				entity_code: search.entity_code,
				exact_status: activeStatus,
				limit: 100,
			}),
		enabled: meQuery.data?.can_view_invoices === true,
	});

	if (meQuery.isLoading) return <div className="p-8 text-sm text-muted-foreground">Laden…</div>;
	if (!meQuery.data?.can_view_invoices) {
		return (
			<div className="mx-auto max-w-md p-8 text-center text-sm">
				<AlertTriangle className="mx-auto mb-2 h-6 w-6 text-destructive" />
				Geen toegang tot inkoopfacturen.
			</div>
		);
	}

	const setSearch = (patch: Partial<Search>) => navigate({ search: () => ({ ...search, ...patch }) });

	return (
		<div className="mx-auto max-w-screen-2xl space-y-6">
			<div>
				<h1 className="text-2xl font-semibold tracking-tight">Inkoopfacturen</h1>
				<p className="text-sm text-muted-foreground">
					{docsQuery.data?.count ?? '…'} factu{(docsQuery.data?.count ?? 0) === 1 ? 'ur' : 'ren'} gevonden
				</p>
			</div>

			{/* Tabs op exact_status */}
			<div className="flex flex-wrap gap-2 border-b">
				{TAB_STATUSES.map((t) => {
					const active = t.key === 'all' ? isAll : !isAll && activeStatus === t.key;
					const Icon = t.icon;
					return (
						<button
							key={t.key}
							type="button"
							onClick={() =>
								setSearch(
									t.key === 'all'
										? { all: true, exact_status: undefined }
										: { all: undefined, exact_status: t.key as ExactStatus },
								)
							}
							className={
								active
									? 'flex items-center gap-1.5 border-b-2 border-primary px-3 py-2 text-sm font-medium text-primary'
									: 'flex items-center gap-1.5 border-b-2 border-transparent px-3 py-2 text-sm text-muted-foreground hover:text-foreground'
							}
						>
							<Icon className="h-4 w-4" />
							{t.label}
						</button>
					);
				})}
			</div>

			{/* Entity filter */}
			<Card>
				<CardContent className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-4">
					<div className="space-y-1.5">
						<Label>Entiteit</Label>
						<Select
							value={search.entity_code ?? ''}
							onChange={(e) => setSearch({ entity_code: e.target.value || undefined })}
						>
							<option value="">Alle</option>
							{entitiesQuery.data?.entities.map((e) => (
								<option key={e.code} value={e.code}>
									{e.name}
								</option>
							))}
						</Select>
					</div>
				</CardContent>
			</Card>

			{search.entity_code && (
				<div className="flex items-center gap-2 text-xs text-muted-foreground">
					<span>Filter:</span>
					<Badge variant="muted" className="gap-1 pr-1">
						{search.entity_code}
						<button
							type="button"
							onClick={() => setSearch({ entity_code: undefined })}
							className="rounded-full p-0.5 hover:bg-muted-foreground/10"
							aria-label="Wis filter"
						>
							<X className="h-3 w-3" />
						</button>
					</Badge>
				</div>
			)}

			<Card>
				<CardContent className="p-0">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Datum</TableHead>
								<TableHead>Entiteit</TableHead>
								<TableHead>Crediteur</TableHead>
								<TableHead>Bestand</TableHead>
								<TableHead className="text-right">Bedrag</TableHead>
								<TableHead>Status</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{docsQuery.isLoading && (
								<TableRow>
									<TableCell colSpan={6} className="h-24 text-center text-sm text-muted-foreground">
										Laden…
									</TableCell>
								</TableRow>
							)}
							{docsQuery.data?.items.map((doc) => (
								<TableRow
									key={doc.id}
									className="cursor-pointer"
									onClick={() => navigate({ to: '/inkoopfacturen/$id', params: { id: doc.id } })}
								>
									<TableCell className="whitespace-nowrap font-mono text-xs text-muted-foreground">
										{formatDateISO(doc.document_date ?? doc.created_at)}
									</TableCell>
									<TableCell className="whitespace-nowrap">
										<Badge variant="muted">{doc.entity_code ?? '—'}</Badge>
									</TableCell>
									<TableCell className="max-w-[16rem] truncate">
										{doc.counterparty_name ?? <span className="text-muted-foreground">—</span>}
									</TableCell>
									<TableCell className="max-w-[12rem] truncate text-xs text-muted-foreground">
										{doc.original_filename}
									</TableCell>
									<TableCell className="whitespace-nowrap text-right font-mono text-xs">
										{formatAmount(doc.amount_total, doc.currency ?? 'EUR')}
									</TableCell>
									<TableCell>
										<ExactStatusBadge status={doc.exact_status} reason={doc.exact_error_reason} />
									</TableCell>
								</TableRow>
							))}
							{docsQuery.data && docsQuery.data.items.length === 0 && (
								<TableRow>
									<TableCell colSpan={6} className="h-24 text-center text-sm text-muted-foreground">
										Geen facturen in deze status.
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</CardContent>
			</Card>

			<div className="text-center">
				<Button variant="ghost" size="sm" asChild>
					<Link to="/">Terug naar dashboard</Link>
				</Button>
			</div>
		</div>
	);
}

function ExactStatusBadge({ status, reason }: { status: ExactStatus | null; reason: string | null }) {
	if (!status) return <Badge variant="muted">—</Badge>;
	const label = EXACT_STATUS_LABELS[status];
	if (status === 'failed') {
		return (
			<span className="inline-flex items-center gap-1.5 text-destructive" title={reason ?? undefined}>
				<AlertCircle className="h-3.5 w-3.5" />
				{label}
			</span>
		);
	}
	if (status === 'sent' || status === 'already_present') {
		return (
			<span className="inline-flex items-center gap-1.5 text-emerald-700">
				<CheckCircle2 className="h-3.5 w-3.5" />
				{label}
			</span>
		);
	}
	return <Badge variant="muted">{label}</Badge>;
}

